/**
 * Game Configuration Constants
 * All game settings, difficulty levels, and scoring rules
 */

export const GAME_STATES = {
  SETUP: 'setup',
  PLAYING: 'playing',
  GAME_OVER: 'gameOver',
};

export const SETUP_STEPS = {
  NAME: 1,
  OPERATIONS: 2,
  DIFFICULTY: 3,
};

export const OPERATORS = {
  PLUS: '+',
  MINUS: '-',
  MULTIPLY: '×',
};

export const DIFFICULTY_LEVELS = {
  EASY: 'let',
  MEDIUM: 'medium',
  HARD: 'svært',
};

/**
 * Game modes
 */
export const GAME_MODES = {
  NORMAL: 'normal',
  TIME_ATTACK: 'timeAttack',
  PRACTICE: 'practice',
  CHALLENGE: 'challenge',
};

/**
 * Game mode configurations
 */
export const GAME_MODE_CONFIG = {
  [GAME_MODES.NORMAL]: {
    key: GAME_MODES.NORMAL,
    label: 'Normal',
    description: 'Klassisk spil med liv og streak',
    icon: '🎮',
    lives: 3,
    hasTimer: false,
    allowMistakes: true,
  },
  [GAME_MODES.TIME_ATTACK]: {
    key: GAME_MODES.TIME_ATTACK,
    label: 'Time Attack',
    description: 'Løs så mange opgaver som muligt på tid!',
    icon: '⏱️',
    lives: 3,
    hasTimer: true,
    timeLimit: 60, // seconds
    timeLimitOptions: [60, 120, 180], // 1min, 2min, 3min
    allowMistakes: true,
  },
  [GAME_MODES.PRACTICE]: {
    key: GAME_MODES.PRACTICE,
    label: 'Øvelse',
    description: 'Øv dig uden liv - perfekt til læring',
    icon: '📚',
    lives: Infinity,
    hasTimer: false,
    allowMistakes: true,
    noHighScore: true, // Don't save high scores in practice mode
  },
  [GAME_MODES.CHALLENGE]: {
    key: GAME_MODES.CHALLENGE,
    label: 'Udfordring',
    description: 'Stigende sværhedsgrad - ingen fejl tilladt!',
    icon: '🏆',
    lives: 1,
    hasTimer: false,
    allowMistakes: false,
    progressiveDifficulty: true,
  },
};

export const INITIAL_LIVES = 3;

export const LEVEL_UP_STREAK = 8; // Level up every 8 correct answers

export const ACHIEVEMENT_THRESHOLDS = {
  STREAK_5: 5,
  STREAK_10: 10,
};

/**
 * Difficulty configurations for number ranges
 */
export const DIFFICULTY_CONFIG = {
  [DIFFICULTY_LEVELS.EASY]: {
    label: 'Let (1-10)',
    description: 'Perfekt for begyndere',
    icon: '🌱',
    multiplication: {
      min: 2,
      max: 6,
    },
    additionSubtraction: {
      num1Min: 5,
      num1Max: 19,
      num2Min: 1,
      num2Max: 9,
    },
    basePoints: 10,
    streakBonus: 20,
  },
  [DIFFICULTY_LEVELS.MEDIUM]: {
    label: 'Medium (1-20)',
    description: 'For dem der er kommet godt i gang',
    icon: '🌿',
    multiplication: {
      min: 3,
      max: 12,
      multiplierMin: 3,
      multiplierMax: 10,
    },
    additionSubtraction: {
      num1Min: 20,
      num1Max: 59,
      num2Min: 5,
      num2Max: 29,
    },
    basePoints: 20,
    streakBonus: 20,
  },
  [DIFFICULTY_LEVELS.HARD]: {
    label: 'Svært (1-50)',
    description: 'For matematikchampions!',
    icon: '🌳',
    multiplication: {
      min: 6,
      max: 20,
      multiplierMin: 4,
      multiplierMax: 15,
    },
    additionSubtraction: {
      num1Min: 30,
      num1Max: 109,
      num2Min: 10,
      num2Max: 59,
    },
    basePoints: 30,
    streakBonus: 20,
  },
};

/**
 * Encouraging messages for correct answers
 */
export const ENCOURAGING_MESSAGES = [
  'Fantastisk!',
  'Godt gået!',
  'Du er en matematikstjerne!',
  'Perfekt!',
  'Sådan!',
  'Du klarer det flot!',
  'Fortsæt sådan!',
];

/**
 * Try again messages for incorrect answers
 */
export const TRY_AGAIN_MESSAGES = [
  'Næsten! Prøv igen!',
  'Så tæt på! En gang til!',
  'Du kan det! Prøv igen!',
  'Ikke helt, men du er på rette vej!',
];

/**
 * Operation configurations
 */
export const OPERATIONS_CONFIG = {
  plus: {
    key: 'plus',
    label: 'Plus opgaver',
    description: 'Læg tal sammen',
    icon: '➕',
    operator: OPERATORS.PLUS,
  },
  minus: {
    key: 'minus',
    label: 'Minus opgaver',
    description: 'Træk tal fra hinanden',
    icon: '➖',
    operator: OPERATORS.MINUS,
  },
  gange: {
    key: 'gange',
    label: 'Gange opgaver',
    description: 'Gang tal med hinanden',
    icon: '✖️',
    operator: OPERATORS.MULTIPLY,
  },
};

/**
 * Default initial state for operations
 */
export const DEFAULT_OPERATIONS = {
  plus: true,
  minus: false,
  gange: false,
};

/**
 * Animation timings (in milliseconds)
 */
export const TIMINGS = {
  CELEBRATION_DURATION: 2000,
  ACHIEVEMENT_DISPLAY: 3000,
  FEEDBACK_DELAY: 200,
};
